import React, { useState, useRef, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Menu,
  Button,
  Form,
  message,
  Dropdown,
  Space,
  Avatar,
} from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  UserOutlined,
  MenuOutlined,
  CloseOutlined,
  LogoutOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { useAuthState } from "react-firebase-hooks/auth";
import LogoImage from "../assets/yno_logo.png";
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  logout,
  auth,
} from "../lib/firebase";
import { userServices } from "../lib/databaseService";
import { mainNavStructure, themeColor } from "./navigationConfig";
import { renderDesktopNavItems, renderHoverSubMenu } from "./DesktopNavigation";
import MobileNavigation from "./MobileNavigation";
import AuthModal from "./AuthModal";

const { Text } = Typography;

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, firebaseAuthLoading] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const [clickedNav, setClickedNav] = useState(null);
  const [form] = Form.useForm();
  const headerRef = useRef(null);

  // Load user data when authenticated
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          const userProfile = await userServices.getUserFromFirestore(user.uid);
          setUserData(userProfile);
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        setUserData(null);
      }
    };

    loadUserData();
  }, [user]);

  // Close sub-menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setClickedNav(null);
        setHoveredNav(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getSelectedKeys = () => {
    const path = location.pathname;
    for (const nav of mainNavStructure) {
      if (nav.pathPrefix && path.startsWith(nav.pathPrefix)) {
        return [nav.key];
      }
      if (nav.path === path) {
        return [nav.key];
      }
    }
    return [];
  };

  // Auth Modal Functions
  const showAuthModal = (signUp = false) => {
    setIsSignUp(signUp);
    setAuthModalVisible(true);
    form.resetFields();
  };

  const handleAuthModalCancel = () => {
    setAuthModalVisible(false);
    form.resetFields();
  };

  const handleEmailAuth = async (values) => {
    setAuthLoading(true);
    try {
      if (isSignUp) {
        const { user: authUser } = await signUpWithEmail(values.email, values.password);
        await userServices.handleNewSignUp(authUser);
        message.success("Sign up successful! Welcome to Yalla Nellab.");
      } else {
        await signInWithEmail(values.email, values.password);
        message.success("Sign in successful! Welcome back.");
      }
      setAuthModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Auth Error:", error);
      message.error(
        error.message ||
          `Failed to ${isSignUp ? "sign up" : "sign in"}. Please try again.`
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthLoading(true);
    try {
      const { user: authUser } = await signInWithGoogle();
      
      // Check if this is a new user (first time Google sign in)
      const existingUser = await userServices.getUserFromFirestore(authUser.uid);
      if (!existingUser) {
        await userServices.handleNewSignUp(authUser);
        message.success("Google sign up successful! Welcome to Yalla Nellab.");
      } else {
        message.success("Signed in with Google successfully!");
      }
      
      setAuthModalVisible(false);
    } catch (error) {
      console.error("Google Auth Error:", error);
      message.error(
        error.message || `Failed to sign ${isSignUp ? "up" : "in"} with Google.`
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const switchAuthMode = () => {
    setIsSignUp(!isSignUp);
    form.resetFields();
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setUserData(null);
      message.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      message.error("Failed to logout");
    }
  };

  // Handle menu item click
  const handleMenuItemClick = (navKey, hasItems) => {
    if (hasItems) {
      setClickedNav(clickedNav === navKey ? null : navKey);
      setHoveredNav(null);
    } else {
      setClickedNav(null);
      setHoveredNav(null);
    }
  };

  // Handle menu item hover
  const handleMenuItemHover = (navKey, hasItems) => {
    if (hasItems && !clickedNav) {
      setHoveredNav(navKey);
    }
  };

  // User dropdown menu items
  const userMenuItems = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile')
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout
    }
  ];

  return (
    <header
      ref={headerRef}
      style={{ height: "170px", width: "100%", position: "relative" }}
    >
      {/* Top Utility Bar */}
      <div
        style={{
          background: "transparent",
          padding: "6px 60px",
          textAlign: "right",
          fontSize: "15px",
          display: window.innerWidth < 768 ? "none" : "block",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "0px",
            right: "0px",
            height: "50px",
            width: "1100px",
            background: "#F4F4F4",
            clipPath: "polygon(5% 0, 100% 0, 100% 100%, 0% 100%)",
            zIndex: 1,
          }}
        />

        <div style={{ position: "relative", zIndex: 2, padding: "2px 20px" }}>
          {user ? (
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />}
                  src={userData?.photoURL}
                  style={{ backgroundColor: themeColor }}
                />
                <Text strong>
                  {userData?.displayName || user.email?.split('@')[0] || 'User'}
                </Text>
              </Space>
            </Dropdown>
          ) : (
            <>
              <Button
                type="link"
                style={{ color: "#000", fontWeight: 600, padding: "0 4px" }}
                onClick={() => showAuthModal(true)}
              >
                Sign Up
              </Button>{" "}
              /{" "}
              <Button
                type="link"
                style={{ color: "#000", fontWeight: 600, padding: "0 4px" }}
                onClick={() => showAuthModal(false)}
              >
                Log in
              </Button>
              <UserOutlined
                style={{ marginLeft: "10px", fontSize: "16px", color: "#000" }}
              />
            </>
          )}
        </div>
      </div>

      {/* Main Header with Navigation */}
      <div
        style={{
          background: "#fff",
          padding: "14px 0",
          width: "100%",
          position: "relative",
          zIndex: 1000,
        }}
      >
        <Row align="middle" justify="space-between">
          <Col
            xs={18}
            sm={18}
            md={10}
            lg={6}
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: window.innerWidth < 768 ? "20px" : "40px",
            }}
          >
            <Link to="/">
              <Row align="middle">
                <img
                  src={LogoImage}
                  alt="YND Logo"
                  style={{
                    height: window.innerWidth < 768 ? "60px" : "80px",
                    width: "auto",
                    marginRight: window.innerWidth < 768 ? "12px" : "16px",
                  }}
                />
                <div
                  style={{
                    display: window.innerWidth < 768 ? "none" : "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    minWidth: "110px",
                  }}
                >
                  <Text style={{fontStyle: "italic" ,color: "#480558", fontSize: "22px",fontWeight: 700, }}>
                    Yalla Nellab
                  </Text>
                  <Text
                    style={{
                      fontStyle: "italic",
                      color: themeColor,
                      fontSize: "22px",
                      color: "#480558",
                      fontWeight: 700,
                    }}
                  >
                    Organization
                  </Text>
                </div>
              </Row>
            </Link>
          </Col>

          <Col
            xs={0}
            sm={0}
            md={14}
            lg={18}
            style={{
              paddingLeft: "20px",
              paddingRight: "0px",
              position: "relative",
            }}
          >
            <Menu
              mode="horizontal"
              selectedKeys={getSelectedKeys()}
              style={{
                borderBottom: "none",
                fontSize: "18px",
                fontWeight: 500,
                background: "transparent",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
              className="no-underline-menu"
            >
              {renderDesktopNavItems({
                mainNavStructure,
                location,
                clickedNav,
                hoveredNav,
                handleMenuItemClick,
                handleMenuItemHover,
                setHoveredNav,
              })}
            </Menu>
          </Col>

          <Col
            xs={6}
            sm={6}
            md={0}
            style={{
              textAlign: "right",
              paddingRight: "20px",
            }}
          >
            <Button
              type="text"
              icon={mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                fontSize: "20px",
                color: themeColor,
                border: "none",
                boxShadow: "none",
              }}
            />
          </Col>
        </Row>
      </div>

      {/* Animated Overlay Sub Menu */}
      {renderHoverSubMenu({
        mainNavStructure,
        clickedNav,
        hoveredNav,
        location,
        setClickedNav,
        setHoveredNav,
      })}

      {/* Mobile Navigation */}
      <MobileNavigation
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        showAuthModal={showAuthModal}
        mainNavStructure={mainNavStructure}
        location={location}
        themeColor={themeColor}
        user={user}
        userData={userData}
        handleLogout={handleLogout}
      />

      {/* Auth Modal */}
      <AuthModal
        authModalVisible={authModalVisible}
        handleAuthModalCancel={handleAuthModalCancel}
        isSignUp={isSignUp}
        authLoading={authLoading}
        form={form}
        handleEmailAuth={handleEmailAuth}
        handleGoogleAuth={handleGoogleAuth}
        switchAuthMode={switchAuthMode}
        themeColor={themeColor}
      />

      {/* Custom CSS */}
      <style jsx>{`
        @media (max-width: 768px) {
          .no-underline-menu {
            display: none;
          }
        }

        /* Remove default Ant Design menu underlines */
        .no-underline-menu .ant-menu-item:hover::after,
        .no-underline-menu .ant-menu-item-active::after,
        .no-underline-menu .ant-menu-submenu:hover::after,
        .no-underline-menu .ant-menu-submenu-active::after {
          display: none !important;
          border-bottom: none !important;
        }

        .no-underline-menu-item:hover {
          border-bottom: none !important;
        }

        .no-underline-menu-item::after {
          display: none !important;
        }

        .ant-menu-horizontal > .ant-menu-item:hover,
        .ant-menu-horizontal > .ant-menu-submenu:hover,
        .ant-menu-horizontal > .ant-menu-item-active,
        .ant-menu-horizontal > .ant-menu-submenu-active {
          border-bottom: none !important;
        }

        /* Custom underline styling with theme color for both hover and selected states */
        .no-underline-menu .ant-menu-item-selected a,
        .no-underline-menu .ant-menu-item a:hover {
          border-bottom: 2px solid #480558 !important;
          color: #480558 !important;
        }

        /* Ensure selected items always show the underline */
        .no-underline-menu .ant-menu-item-selected a {
          border-bottom: 2px solid #480558 !important;
          color: #480558 !important;
        }

        /* Animated overlay menu */
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animated-overlay-menu {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </header>
  );
};

export default Header;